/**
 * Questionnaire structure for the SELLER blind-teaser flow.
 * Covers stages 1..3 ONLY (15 questions).
 * Stage 4 (Teaser) is AUTO-GENERATED by AI after stage 3 is completed.
 */

import { CLOSED_YEARS } from "./stages";
import { t } from "../lib/i18n";

export const QUESTIONS = [
  // ---- Stage 1: Información Básica ----
  {
    stage: 1, key: "year_founded", type: "number",
    title: t("¿En qué año se fundó la empresa?"),
    helper: t("Solo el año. Te servirá para mostrar la trayectoria y madurez."),
    placeholder: "p. ej. 1998",
  },
  {
    stage: 1, key: "employees", type: "number",
    title: t("¿Cuántos empleados tiene actualmente?"),
    helper: t("Aproximado está bien. Es uno de los primeros criterios de cualificación."),
    placeholder: "p. ej. 24",
  },
  {
    stage: 1, key: "ownership", type: "text",
    title: t("¿Quién es el accionariado?"),
    helper: t("Familiar, fondos, varios socios, fundador único... No expongas nombres concretos."),
    placeholder: t("p. ej. 100% familiar / 60% fundador, 40% socios"),
  },
  {
    stage: 1, key: "website", type: "text",
    title: t("¿Tienes web corporativa?"),
    helper: t("Solo la usaremos para verificación interna. No se mostrará a nadie sin tu permiso."),
    placeholder: t("https://"),
    optional: true,
  },
  {
    stage: 1, key: "short_description", type: "textarea",
    title: t("Describe la empresa en una frase"),
    helper: t("Imagina que la tienes que explicar a alguien en el ascensor. Sin nombres propios."),
    placeholder: t("p. ej. Distribución mayorista de productos industriales a clientes B2B en el norte de España."),
  },

  // ---- Stage 2: Financieros ----
  {
    stage: 2, key: "revenue_y1_eur", type: "number",
    title: `¿Cuál fue la facturación del ejercicio ${CLOSED_YEARS[0]}? (€)`,
    helper: t("Primero de los 3 ejercicios cerrados. Así mostramos la evolución histórica antes de la proyección."),
    placeholder: "p. ej. 3900000",
  },
  {
    stage: 2, key: "revenue_y2_eur", type: "number",
    title: `¿Cuál fue la facturación del ejercicio ${CLOSED_YEARS[1]}? (€)`,
    helper: t("Segundo ejercicio cerrado."),
    placeholder: "p. ej. 4200000",
  },
  {
    stage: 2, key: "revenue_eur", type: "number",
    title: `¿Cuál fue la facturación del último ejercicio cerrado (${CLOSED_YEARS[2]})? (€)`,
    helper: t("Cifra aproximada en euros. La mostraremos en rangos a los searchers."),
    placeholder: "p. ej. 4500000",
  },
  {
    stage: 2, key: "gross_eur", type: "number",
    title: `¿Cuál fue el beneficio bruto del último ejercicio (${CLOSED_YEARS[2]})? (€)`,
    helper: t("Facturación − coste de ventas. Calculamos automáticamente tu margen bruto a partir de este dato."),
    placeholder: "p. ej. 2200000",
    optional: true,
  },
  {
    stage: 2, key: "ebitda_y1_eur", type: "number",
    title: `¿Cuál fue el EBITDA del ejercicio ${CLOSED_YEARS[0]}? (€)`,
    helper: t("Resultado operativo antes de intereses, impuestos y amortizaciones."),
    placeholder: "p. ej. 650000",
  },
  {
    stage: 2, key: "ebitda_y2_eur", type: "number",
    title: `¿Cuál fue el EBITDA del ejercicio ${CLOSED_YEARS[1]}? (€)`,
    helper: t("Segundo ejercicio cerrado."),
    placeholder: "p. ej. 740000",
  },
  {
    stage: 2, key: "ebitda_eur", type: "number",
    title: `¿Cuál fue el EBITDA del último ejercicio cerrado (${CLOSED_YEARS[2]})? (€)`,
    helper: t("El margen EBITDA se calcula automáticamente a partir de este dato."),
    placeholder: "p. ej. 850000",
  },
  {
    stage: 2, key: "growth_pct", type: "number",
    title: t("¿Cuánto crece la empresa anualmente? (%)"),
    helper: t("Crecimiento medio de ventas en los últimos años."),
    placeholder: "p. ej. 8",
  },
  {
    stage: 2, key: "net_debt_eur", type: "number",
    title: t("¿Cuál es la deuda neta actual? (€)"),
    helper: t("Deuda financiera menos caja. Si es caja neta positiva, pon número negativo."),
    placeholder: "p. ej. 200000",
  },

  // ---- Stage 3: Cualificación ----
  {
    stage: 3, key: "recurrence", type: "select",
    title: t("¿Qué porcentaje de los ingresos es recurrente?"),
    helper: t("Recurrencia = ingresos que se repiten año a año con los mismos clientes."),
    options: ["Alta (>70%)", "Media (30-70%)", "Baja (<30%)"],
  },
  {
    stage: 3, key: "customer_concentration", type: "number",
    title: t("¿Qué % de la facturación viene de los 5 mayores clientes?"),
    helper: t("Mide la concentración. Cuanto menor, mejor para un searcher."),
    placeholder: "p. ej. 35",
  },
  {
    stage: 3, key: "competitive_moat", type: "textarea",
    title: t("¿Qué hace especial a tu empresa frente a competidores?"),
    helper: t("Ventaja competitiva: marca, know-how, contratos, ubicación, tecnología propia..."),
    placeholder: t("Describe en 2-3 frases sin nombres propios."),
  },
  {
    stage: 3, key: "management_continuity", type: "select",
    title: t("¿El equipo directivo se quedaría tras la venta?"),
    helper: t("Clave para un searcher: ¿hay un equipo sólido que opere sin el dueño actual?"),
    options: ["Sí", "Parcial", "No"],
  },
  {
    stage: 3, key: "transferability", type: "select",
    title: t("¿Cómo de transferible es el negocio?"),
    helper: t("¿Funciona sin la presencia diaria del dueño? Alta = el dueño puede salir sin impacto."),
    options: ["Alta", "Media", "Baja"],
  },
];

export function questionsForStage(stage) {
  return QUESTIONS.filter((q) => q.stage === stage);
}
